import { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Download } from "lucide-react";
import { actingAlbum, posterAlbum } from "@/data/content";
import { Eyebrow, Reveal } from "./Reveal";
import { Lightbox } from "./Lightbox";

const downloadable = (it) => it.kind !== "Work";

const COVER = "/img/hero-mimi.jpg";

const AlbumCover = ({ cover, count, onOpen, testid, label, pos = "50% 0%" }) => (
  <button data-testid={testid} onClick={onOpen} aria-label={`Open ${label} album`}
    className="group relative block w-full aspect-[4/5] text-left mr-3 sm:mr-5">
    <span className="absolute inset-0 translate-x-2 translate-y-2 sm:translate-x-4 sm:translate-y-4 bg-[var(--nude)] border border-black/5 group-hover:translate-x-3 group-hover:translate-y-3 sm:group-hover:translate-x-6 sm:group-hover:translate-y-6 transition-transform duration-700" />
    <span className="absolute inset-0 translate-x-1 translate-y-1 sm:translate-x-2 sm:translate-y-2 bg-white border border-black/5 group-hover:translate-x-1.5 group-hover:translate-y-1.5 sm:group-hover:translate-x-3 sm:group-hover:translate-y-3 transition-transform duration-700" />
    <span className="absolute inset-0 overflow-hidden shadow-[0_30px_60px_-30px_rgba(60,40,20,0.45)]">
      <img src={cover} alt={`${label} album cover`} style={{ objectPosition: pos }} className="h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms] ease-out" />
      <span className="absolute bottom-0 inset-x-0 p-3 sm:p-5 bg-gradient-to-t from-black/55 to-transparent flex items-end justify-between gap-2 text-white">
        <span className="hidden sm:inline text-[12px] uppercase tracking-[0.22em]">View album</span>
        <span data-testid={`${testid}-count`} className="text-[12px] tracking-[0.2em]">{String(count).padStart(2, "0")} {count === 1 ? "PHOTO" : "PHOTOS"}</span>
      </span>
    </span>
  </button>
);

const AlbumTile = ({ title, ...props }) => (
  <div className="pr-2 sm:pr-4">
    <AlbumCover label={title} {...props} />
    <p className="mt-6 font-display text-xl sm:text-2xl text-stone-900">{title}</p>
  </div>
);

export const ResumeDownload = () => (
  <a data-testid="acting-resume-download" href="/mimi-tao-resume.pdf" download="Mimi-Tao-Acting-Resume.pdf"
    className="group inline-flex items-center gap-4 bg-[var(--ink)] text-[var(--bg)] px-7 py-4 hover:bg-[var(--gold)] transition-colors duration-500">
    <Download size={16} className="group-hover:translate-y-0.5 transition-transform duration-300" />
    <span className="flex flex-col text-left">
      <span className="text-[12px] uppercase tracking-[0.22em]">Download résumé</span>
      <span className="text-[12px] text-white/75">Credits, stats, skills & photos · PDF</span>
    </span>
  </a>
);

export const ActingAlbum = () => {
  const [open, setOpen] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const items = actingAlbum.map((a) => ({ ...a, download: downloadable(a) }));
  const start = Math.max(0, items.findIndex((a) => a.src === COVER));
  const lists = { portraits: items, posters: posterAlbum };
  return (
    <div data-testid="acting-album" className="mt-24 grid md:grid-cols-12 gap-12 md:gap-8 items-end">
      <Reveal className="md:col-span-7 grid grid-cols-2 gap-4 sm:gap-8">
        <AlbumTile title="Portraits & work" testid="acting-album-cover" cover={COVER} count={items.length} onOpen={() => setOpen({ list: "portraits", start })} />
        <AlbumTile title="Posters" testid="acting-posters-cover" cover={posterAlbum[0].src} pos="50% 40%" count={posterAlbum.length} onOpen={() => setOpen({ list: "posters", start: 0 })} />
      </Reveal>
      <Reveal className="md:col-span-4 md:col-start-9 pb-2" delay={0.1}>
        <Eyebrow className="mb-4">Albums</Eyebrow>
        <h3 className="font-display text-3xl sm:text-5xl">Portraits <em className="text-[var(--gold)]">&</em> work.</h3>
        <p className="mt-5 max-w-sm text-stone-600 font-light leading-relaxed">Headshots, digitals, moments from set and key art. Tap an album to browse.</p>
        <a data-testid="acting-headshots-download" href="/mimi-tao-headshots.zip" download="Mimi-Tao-Headshots.zip"
          className="mt-8 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-stone-700 hover:text-[var(--gold)] transition-colors duration-300">
          <Download size={14} className="text-[var(--gold)]" /> Download headshots
        </a>
      </Reveal>
      <AnimatePresence>{open !== null && <Lightbox items={lists[open.list]} start={open.start} onClose={close} />}</AnimatePresence>
    </div>
  );
};
